
import { memo } from 'react';
import { Clock, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WorkflowCard } from '../WorkflowCard';
import { aslAccentColors } from '../WorkflowCard.types';
import { useNodeSettings } from '../NodeSettingsContext';
import { type StateNodeData } from '@/types/workflow-graph';

interface WaitNodeProps {
    data: StateNodeData;
    selected?: boolean;
}

export const WaitNode = memo(function WaitNode({ data, selected }: WaitNodeProps) {
    const { openSettings } = useNodeSettings();
    const { label, state, isStart, isEnd, layoutDirection } = data;

    // Format wait duration for display
    const waitInfo = state.Seconds
        ? `${state.Seconds}s`
        : state.Timestamp
            ? `Until ${state.Timestamp}`
            : state.SecondsPath
                ? `$.${state.SecondsPath}`
                : 'Configured';

    return (
        <WorkflowCard
            selected={selected}
            accentColor={aslAccentColors.wait}
            isStart={isStart}
            isEnd={isEnd}
            handleColor="!bg-orange-400 !border-orange-500"
            layoutDirection={layoutDirection}
        >
            <WorkflowCard.Icon>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-orange-100 text-orange-600">
                    <Clock size={20} />
                </div>
            </WorkflowCard.Icon>

            <WorkflowCard.Name>{label}</WorkflowCard.Name>

            <WorkflowCard.Description>
                {state.Comment ? (
                    <p className="leading-tight">{state.Comment}</p>
                ) : (
                    <p className="leading-tight font-medium text-orange-600">⏱ {waitInfo}</p>
                )}
            </WorkflowCard.Description>

            <WorkflowCard.BottomRight>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    title="Node Settings"
                    onClick={(e) => {
                        e.stopPropagation();
                        openSettings(data);
                    }}
                >
                    <Settings size={14} />
                </Button>
            </WorkflowCard.BottomRight>
        </WorkflowCard>
    );
});
